// Artificial Grass Products - Supreme Turf Approved Products
// SKU Format: AG-[PILEHEIGHT]-[COLOURCODE]-[WIDTH]

export interface GrassProduct {
  id: string;
  name: string;
  skuBase: string;
  colourCode: string;
  colour: string;
  pileHeight: number;
  category: string;
  pricePerSqm: number;
  availableWidths: number[];
  description: string;
  metaTitle?: string;
  metaDescription?: string;
  outOfStock?: boolean;
}

// Width options in meters
export const GRASS_WIDTH_OPTIONS = [2, 3.6, 4];

// Length options in meters (1m to 25m)
export const GRASS_LENGTH_OPTIONS = Array.from({ length: 25 }, (_, i) => i + 1);

// Colour code mapping
export const COLOUR_CODES: Record<string, string> = {
  'OG': 'Olive Green',
  'LG': 'Lush Green', 
  'NG': 'Natural Green',
  'BG': 'Buffalo Green',
  'FRG': 'Forest Green'
};

export const GRASS_PRODUCTS: GrassProduct[] = [
  {
    id: "15mm-sports-grass-olive-green",
    name: "15mm Sports Grass – Olive Green",
    skuBase: "AG-15-OG",
    colourCode: "OG",
    colour: "Olive Green",
    pileHeight: 15,
    category: "Artificial Grass",
    pricePerSqm: 25,
    availableWidths: [2, 4],
    description: "Highly dense Artificial Turf for Sports fields, Cricket ground, Playgrounds, Gyms, Showrooms, Commercial areas, Putt-Putt, Backyards, Pool Areas etc. Shorter pile height and high density provide perfect underfoot and bounce required for sports.",
    metaTitle: "15mm Sports Grass Olive Green | $25 per m² | Supreme Turf",
    metaDescription: "Buy 15mm sports grass olive green at $25 per m². Available in 2m & 4m widths. Trade & bulk pricing available Australia-wide."
  },
  {
    id: "30mm-forest-green-artificial-grass",
    name: "30mm Forest Green Artificial Grass",
    skuBase: "AG-30-FRG",
    colourCode: "FRG",
    colour: "Forest Green",
    pileHeight: 30,
    category: "Artificial Grass",
    pricePerSqm: 16,
    availableWidths: [2, 3.6, 4],
    description: "Our 30mm Artificial Turf in Forest Green delivers a vibrant, crisp lawn finish with a lively two-tone blade colour that brings any outdoor space to life. The 30mm pile height offers a soft, comfortable feel underfoot with strong durability for everyday family use, making it ideal for backyards, balconies, pool surrounds, pet areas, and landscaping projects across Australia.",
    metaTitle: "30mm Forest Green Artificial Grass | $16 per m² | Supreme Turf",
    metaDescription: "Buy 30mm forest green artificial grass at $16 per m². Available in 2m, 3.6m & 4m widths. Trade & bulk pricing available Australia-wide."
  },
  {
    id: "40mm-lush-green-artificial-grass",
    name: "40mm Lush Green Artificial Grass",
    skuBase: "AG-40-LG",
    colourCode: "LG",
    colour: "Lush Green",
    pileHeight: 40,
    category: "Artificial Grass",
    pricePerSqm: 20,
    availableWidths: [2, 3.6, 4],
    description: "Our 40mm Artificial Turf in Lush Green is designed to create a thick, luxurious lawn with a rich, vibrant finish. The longer pile height provides a soft, cushioned feel underfoot while delivering a dense, premium look that enhances any outdoor space. The lush green colour gives the appearance of a healthy, well-watered lawn all year round, adding instant freshness and visual appeal.",
    metaTitle: "40mm Lush Green Artificial Grass | $20 per m² | Supreme Turf",
    metaDescription: "Buy 40mm lush green artificial grass at $20 per m². Available in 3.6m & 4m widths. Trade & bulk pricing available Australia-wide."
  },
  {
    id: "40mm-natural-green-artificial-grass",
    name: "40mm Natural Green Artificial Grass",
    skuBase: "AG-40-NG",
    colourCode: "NG",
    colour: "Natural Green",
    pileHeight: 40,
    category: "Artificial Grass",
    pricePerSqm: 20,
    availableWidths: [2, 3.6, 4],
    description: "Our 40mm Artificial Turf in Natural Green is crafted to deliver a full, luxurious lawn with exceptional realism and comfort. The longer pile height creates a dense, soft surface that closely mimics the look and feel of freshly grown natural grass, making it ideal for premium backyards, landscaped gardens, and outdoor entertaining areas.",
    metaTitle: "40mm Natural Green Artificial Grass | $20 per m² | Supreme Turf",
    metaDescription: "Buy 40mm natural green artificial grass at $20 per m². Available in 3.6m & 4m widths. Trade & bulk pricing available Australia-wide."
  },
  {
    id: "45mm-buffalo-green-artificial-grass",
    name: "45mm Buffalo Green Artificial Grass",
    skuBase: "AG-45-BG",
    colourCode: "BG",
    colour: "Buffalo Green",
    pileHeight: 45,
    category: "Artificial Grass",
    pricePerSqm: 22,
    availableWidths: [2, 3.6, 4],
    description: "Experience the ultimate in realism with our 45mm Premium Artificial Turf in Buffalo Green — the most ultra-realistic green lawn ever made. With its extra-long, dense pile, this turf delivers an exceptionally lush, soft, and natural feel underfoot, perfectly replicating the texture and movement of real grass. The rich Buffalo Green colour adds depth and vibrancy, creating a striking, premium finish that elevates any backyard, garden, or outdoor space.",
    metaTitle: "45mm Buffalo Green Artificial Grass | $22 per m² | Supreme Turf",
    metaDescription: "Buy 45mm buffalo green artificial grass at $22 per m². Available in 3.6m & 4m widths. Trade & bulk pricing available Australia-wide."
  }
];

export function getGrassProductById(id: string): GrassProduct | undefined {
  return GRASS_PRODUCTS.find(product => product.id === id);
}

export function calculateGrassPrice(pricePerSqm: number, width: number, length: number): number {
  const area = width * length;
  return area * pricePerSqm;
}

export function generateSKU(skuBase: string, width: number): string {
  // Convert width to SKU format (e.g., 3.6 -> 36M, 4 -> 4M, 2 -> 2M)
  const widthCode = width === 3.6 ? '36M' : `${width}M`;
  return `${skuBase}-${widthCode}`;
}

export function isGrassProduct(productTitle: string): boolean {
  const grassKeywords = ['turf', 'grass', 'lawn', 'artificial grass'];
  const lowerTitle = productTitle.toLowerCase();
  return grassKeywords.some(keyword => lowerTitle.includes(keyword));
}

export function matchGrassProduct(productTitle: string): GrassProduct | undefined {
  const lowerTitle = productTitle.toLowerCase();
  
  // Match by pile height and colour
  for (const product of GRASS_PRODUCTS) {
    const pileHeightMatch = lowerTitle.includes(`${product.pileHeight}mm`);
    const colourMatch = lowerTitle.includes(product.colour.toLowerCase());
    
    if (pileHeightMatch && colourMatch) {
      return product;
    }
  }
  
  // Fallback: check for pile height and type keywords
  if (lowerTitle.includes('15mm') && (lowerTitle.includes('sport') || lowerTitle.includes('olive'))) {
    return GRASS_PRODUCTS.find(p => p.id === '15mm-sports-grass-olive-green');
  }
  if (lowerTitle.includes('30mm') && lowerTitle.includes('forest')) {
    return GRASS_PRODUCTS.find(p => p.id === '30mm-forest-green-artificial-grass');
  }

  if (lowerTitle.includes('40mm') && lowerTitle.includes('lush')) {
    return GRASS_PRODUCTS.find(p => p.id === '40mm-lush-green-artificial-grass');
  }
  if (lowerTitle.includes('40mm') && lowerTitle.includes('natural')) {
    return GRASS_PRODUCTS.find(p => p.id === '40mm-natural-green-artificial-grass');
  }
  if (lowerTitle.includes('45mm') || lowerTitle.includes('buffalo')) {
    return GRASS_PRODUCTS.find(p => p.id === '45mm-buffalo-green-artificial-grass');
  }
  
  // Generic grass match - return 40mm Lush Green as default
  if (isGrassProduct(productTitle)) {
    return GRASS_PRODUCTS.find(p => p.id === '40mm-lush-green-artificial-grass');
  }
  
  return undefined;
}
